import { useMutation, useQueryClient } from 'react-query'
import callAPI from '../../../API/callSecureAPI'
import endpoints from '../../../API/endpoints'
import { useAuth0 } from '../../../contexts/auth0-context'
import { useStrategy } from '../../hooks/useStrategy'
import useInvalidateLinkingQueries from '../linking/useInvalidateLinkingQueries'
import { getStrategicImperativesQueryKey, StrategicImperative } from './useStrategicImperativesQuery'

const AUDIENCE = { audience: 'https://atlas.aesara.com' }

export const useCreateStrategicImperativeMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()
    const strategyId = useStrategy()?._id
    const { invalidateLinkingQueries } = useInvalidateLinkingQueries()

    const createStrategicImperative = async (data: Record<string, unknown>) => {
        const token = await getTokenSilently(AUDIENCE)
        const { url, method } = endpoints.createStrategicImperative
        const response = await callAPI(url, method, token, data)

        if (!response.success) {
            throw new Error(response.error ?? 'Failed to create the strategic imperative')
        }

        return response
    }

    return useMutation(createStrategicImperative, {
        onSuccess: async () => {
            await queryClient.invalidateQueries(getStrategicImperativesQueryKey(strategyId))
            invalidateLinkingQueries()
        },
    })
}

export const useReorderStrategicImperativesMutation = () => {
    const { getTokenSilently } = useAuth0()
    const queryClient = useQueryClient()
    const strategyId = useStrategy()?._id
    const queryKey = getStrategicImperativesQueryKey(strategyId)

    const reorderStrategicImperatives = async (strategicImperatives: StrategicImperative[]) => {
        const token = await getTokenSilently(AUDIENCE)
        const { url, method } = endpoints.reorderStrategicImperatives
        const response = await callAPI(
            url,
            method,
            token,
            strategicImperatives.map(({ _id }) => _id)
        )

        if (!response.success) {
            throw new Error(response.error ?? 'Failed to save the new order')
        }

        return response
    }

    return useMutation(reorderStrategicImperatives, {
        onMutate: async (strategicImperatives) => {
            await queryClient.cancelQueries(queryKey)

            const previous = queryClient.getQueryData<StrategicImperative[]>(queryKey)

            queryClient.setQueryData(
                queryKey,
                strategicImperatives.map((strategicImperative, index) => ({ ...strategicImperative, order: index }))
            )

            return { previous }
        },
        onError: (_error, _variables, context) => {
            const previous = (context as { previous?: StrategicImperative[] } | undefined)?.previous

            if (previous) {
                queryClient.setQueryData(queryKey, previous)
            }
        },
        onSettled: () => queryClient.invalidateQueries(queryKey),
    })
}
